'use client';

import { useRouter } from '@bprogress/next';
import FormInput from '@c/forms/controls/FormInput';
import FormLabel from '@c/forms/controls/FormLabel';
import FormTextarea from '@c/forms/controls/FormTextarea';
import HelpCenterHeader from '@c/headers/helpcenter/HelpCenterHeader';
import { ChattingAppIcon, EmailFeatureIcon, SmartphoneIcon } from '@c/icons';
import ChatModal from '@c/modals/ChatModal';
import RequestCallModal from '@c/modals/RequestCallModal';
import DynamicToast from '@c/toasts/DynamicToast';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@ui/Button';
import { contactUs } from '@util/firestore/messages';
import { ADMIN_UID } from '@util/support';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const HelpCategoryCard = ({
  title,
  description,
  onClick,
  icon,
  buttonText,
  href,
}: {
  title: string;
  description: string;
  onClick?: () => void;
  href?: string;
  icon: React.ReactElement;
  buttonText: string;
}) => (
  <div className="flex w-full grow flex-col items-center justify-evenly gap-[3.2rem] rounded-[0.6rem] p-[4rem] shadow-lg">
    {icon}
    <div className="flex flex-col items-center gap-[1.2rem]">
      <p className="text-center text-[2.4rem] font-semibold leading-[3.2rem]">
        {title}
      </p>
      <p className="text-center text-[1.6rem] leading-[2.4rem]">
        {description}
      </p>
    </div>
    <Button type="secondary" onClick={onClick} text={buttonText} href={href} />
  </div>
);

const checkoutInfoFormSchema = z.object({
  email: z.string().email(),
  name: z.string(),
  message: z.string(),
  phone: z.string(),
});

export type ContactUsForm = z.infer<typeof checkoutInfoFormSchema>;

const ContactUs = () => {
  const [loading, setLoading] = useState(false);
  const { userDoc } = useAuth();
  const { showToast } = useToastContext();
  const {
    reset,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ContactUsForm>({
    resolver: zodResolver(checkoutInfoFormSchema),
  });

  useEffect(() => {
    if (userDoc?.email) setValue('email', userDoc?.email);
    if (userDoc?.name) setValue('name', userDoc?.name);
  }, [userDoc]);

  async function onSubmit(data: ContactUsForm) {
    try {
      if (data.message) {
        setLoading(true);
        await contactUs(data);
        setLoading(false);
        showToast(<DynamicToast text={'Message sent'} />, {
          position: 'bottom-center',
          style: {
            borderRadius: '16px',
            backgroundColor: '#FDF4E9',
            borderStyle: 'solid',
            borderWidth: '1px',
            borderColor: '#EF9021',
            width: 'min-content',
          },
        });
        reset();
      }
    } catch (e: any) {
      console.error(e);
      showToast(
        <DynamicToast
          text={'Failed to send. Please email support support@mxlocker.com'}
          error
        />,
        {
          position: 'bottom-center',
          style: {
            borderRadius: '16px',
            backgroundColor: '#FDF4E9',
            borderStyle: 'solid',
            borderWidth: '1px',
            borderColor: '#EF9021',
            width: 'min-content',
          },
        }
      );
    }
  }
  const [showForm, setShowForm] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const router = useRouter();
  return (
    <>
      <Head>
        <title>MX Locker | Contact</title>
      </Head>

      <ChatModal
        isOpen={showChatModal}
        dismiss={() => setShowChatModal(false)}
        recipientUid={ADMIN_UID}
      />

      <RequestCallModal
        isOpen={showCallModal}
        dismiss={() => setShowCallModal(false)}
      />

      <HelpCenterHeader />

      <div className="mx-auto flex max-w-[144rem] flex-col gap-[6rem] px-[2rem] py-[6rem] sm:px-[12rem]">
        <div className="flex flex-col items-center gap-[1.6rem]">
          <h1 className="text-center text-[3.2rem] font-semibold leading-[4.2rem]">
            Contact Us
          </h1>
          <p className="text-center text-[1.5rem] leading-[2.2rem]">
            Didn’t find the answer you were looking for? Our team at MX Locker
            is here to help. Reach out anytime using any of the contact methods
            below — we’ll make sure you’re taken care of.
          </p>
        </div>
        {!showForm ? (
          <div className="grid gap-[2.4rem] sm:grid-cols-3">
            <HelpCategoryCard
              title="Chat with us"
              description="If you have a MX Locker account, chat with our customer support team available 10am-5pm EST (Mon-Fri)."
              onClick={() => {
                if (userDoc) {
                  setShowChatModal(true);
                } else {
                  router.push('/login?redirect=/contact');
                }
              }}
              icon={<ChattingAppIcon />}
              buttonText="Chat Now"
            />

            <HelpCategoryCard
              title="Send us an email"
              description="Need help with an order or account? Send us an email and we’ll get back to you as soon as possible."
              icon={<EmailFeatureIcon />}
              buttonText="Send Email"
              onClick={() => setShowForm(true)}
            />

            {/* <HelpCategoryCard
              title="Text us"
              description="Our team is available to respond to your text messages and provide the answers."
              onClick={() => {
                window.location.href = 'sms:+14077836166';
              }}
              icon={<SmartphoneIcon />}
              buttonText="Text Us"
            /> */}
            {/* (407) 783-6166 */}
            <HelpCategoryCard
              title="Speak with us"
              description="Need personal support? Call or text us at (407) 783-6166 and our team will make sure you get the help you need. Phone support is available Monday–Friday, 10 AM – 5 PM EST."
              icon={<SmartphoneIcon />}
              buttonText="Call Us"
              href="tel:4077836166"
            />
          </div>
        ) : (
          <form
            className="mx-auto mt-[3rem] flex w-full max-w-[40rem] flex-col gap-16"
            onSubmit={handleSubmit(onSubmit)}
          >
            <FormLabel value="Email" required>
              <FormInput
                type="email"
                placeholder="Enter your email"
                error={!!errors.email}
                {...register('email')}
                autoComplete="email"
              />
            </FormLabel>
            <FormLabel value="Name" required>
              <FormInput
                type="text"
                placeholder="Enter your name"
                error={!!errors.name}
                {...register('name')}
                autoComplete="name"
              />
            </FormLabel>
            <FormLabel value="Phone" required>
              <FormInput
                type="tel"
                placeholder="Enter your phone number"
                error={!!errors.phone}
                {...register('phone')}
                autoComplete="tel"
              />
            </FormLabel>
            <FormLabel value="Message" required>
              <FormTextarea {...register('message')} error={!!errors.message} />
            </FormLabel>
            <div className="mx-auto flex flex-col justify-between gap-4 sm:flex-row">
              <Button
                text="Send Email"
                type="secondary"
                buttonType="submit"
                loading={loading}
              />
            </div>
          </form>
        )}
      </div>
    </>
  );
};

export default ContactUs;
